import React, { useEffect, useState } from "react";
import managedAxios, { isCancelError } from "@/utils/managedAxios";
import { useAuth } from "@/auth/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { API_BASE } from "@/App";
import { Calendar, Target, BookOpen, TrendingUp, Sparkles, Lock } from "lucide-react";

const MentorPage = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [basicPlan, setBasicPlan] = useState(null);
  const [personalizedPlan, setPersonalizedPlan] = useState(null);
  const [showPersonalized, setShowPersonalized] = useState(false);
  const [loading, setLoading] = useState(false);

  const isPro = user?.role === "PRO";

  useEffect(() => {
    const load = async () => {
      try {
        const { data } = await managedAxios.get(`${API_BASE}/mentor/plan-7-day`);
        setBasicPlan(data);
      } catch (e) {
        console.error("Error loading basic plan:", e);
      }
    };
    load();
  }, []);

  const handlePersonalizedPlan = async () => {
    if (!isPro) {
      toast({
        variant: "destructive",
        description: "🔒 Personalized mentor plans are PRO-only. Upgrade to unlock AI-powered personalized study plans."
      });
      return;
    }

    setLoading(true);
    try {
      const { data } = await managedAxios.get(`${API_BASE}/mentor/plan-personalized`);
      setPersonalizedPlan(data);
      setShowPersonalized(true);
      toast({ description: "Personalized plan generated successfully!" });
    } catch (err) {
      const msg = err?.response?.data?.detail;
      toast({
        variant: "destructive",
        description: msg || "Could not generate personalized plan."
      });
    } finally {
      setLoading(false);
    }
  };

  const handleWeeklyPlan = async () => {
    if (!isPro) {
      toast({
        variant: "destructive",
        description: "🔒 Weekly strategic plans are PRO-only. Upgrade to unlock detailed weekly planning."
      });
      return;
    }

    setLoading(true);
    try {
      const { data } = await managedAxios.get(`${API_BASE}/mentor/plan-weekly`);
      toast({ description: "Weekly plan generated!" });
      console.log("Weekly plan:", data);
    } catch (err) {
      const msg = err?.response?.data?.detail;
      toast({
        variant: "destructive",
        description: msg || "Could not generate weekly plan."
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 py-8 space-y-6" data-testid="mentor-page">
      {/* Header */}
      <div className="bg-gradient-to-r from-indigo-600 via-purple-600 to-fuchsia-600 rounded-2xl p-8 text-white">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold mb-2 flex items-center gap-3">
              <Sparkles className="w-8 h-8" />
              AI Mentor Mode
            </h1>
            <p className="text-indigo-100">
              Turn your week into a clear, AI-guided plan across GS, Optional and revision
            </p>
          </div>
          <Badge className={`px-4 py-2 text-sm ${isPro ? 'bg-amber-500' : 'bg-white/20'}`}>
            {isPro ? '⭐ PRO Mentor' : '🔓 Basic Plan'}
          </Badge>
        </div>
      </div>

      {/* Plan Type Selection */}
      <div className="flex flex-wrap gap-3">
        <Button
          onClick={() => setShowPersonalized(false)}
          variant={!showPersonalized ? "default" : "outline"}
          className="flex items-center gap-2"
        >
          <Calendar className="w-4 h-4" />
          7-Day Starter Plan
        </Button>
        <Button
          onClick={handlePersonalizedPlan}
          variant={showPersonalized ? "default" : "outline"}
          className="flex items-center gap-2"
          disabled={loading}
        >
          {isPro ? <Target className="w-4 h-4" /> : <Lock className="w-4 h-4" />}
          {loading ? "Generating..." : "Personalized Plan"}
          {!isPro && <Badge className="ml-2 bg-amber-500 text-white text-[10px]">PRO</Badge>}
        </Button>
        <Button
          onClick={handleWeeklyPlan}
          variant="outline"
          className="flex items-center gap-2"
          disabled={loading}
        >
          {isPro ? <TrendingUp className="w-4 h-4" /> : <Lock className="w-4 h-4" />}
          Weekly Strategic Plan
          {!isPro && <Badge className="ml-2 bg-amber-500 text-white text-[10px]">PRO</Badge>}
        </Button>
      </div>

      {/* 7-Day Basic Plan */}
      {!showPersonalized && basicPlan && (
        <Card className="border-slate-200 bg-white shadow-sm">
          <CardContent className="p-6 space-y-6">
            <div className="flex items-center gap-3">
              <div className="bg-indigo-100 p-2 rounded-lg">
                <Calendar className="w-5 h-5 text-indigo-600" />
              </div>
              <div>
                <h2 className="text-lg font-bold text-slate-900">7-Day Starter Plan</h2>
                <p className="text-xs text-slate-600">Auto-generated by Ranker Mentor AI</p>
              </div>
            </div>

            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3" data-testid="mentor-plan-grid">
              {basicPlan.plan.map((day, idx) => {
                const colors = [
                  'from-blue-50 to-indigo-50 border-blue-200',
                  'from-purple-50 to-fuchsia-50 border-purple-200',
                  'from-emerald-50 to-teal-50 border-emerald-200',
                  'from-amber-50 to-orange-50 border-amber-200',
                  'from-rose-50 to-pink-50 border-rose-200',
                  'from-cyan-50 to-blue-50 border-cyan-200',
                  'from-green-50 to-emerald-50 border-green-200'
                ];
                return (
                  <Card
                    key={day.day}
                    className={`bg-gradient-to-br ${colors[idx % 7]} border-2`}
                    data-testid={`mentor-day-${day.day}`}
                  >
                    <CardContent className="p-4 space-y-3">
                      <div className="flex items-center justify-between">
                        <Badge className="bg-white/80 text-slate-900 text-xs">Day {day.day}</Badge>
                        <BookOpen className="w-4 h-4 text-slate-600" />
                      </div>
                      <h3 className="text-sm font-bold text-slate-900">{day.focus}</h3>
                      <ul className="space-y-1.5">
                        {day.tasks.map((t, i) => (
                          <li key={i} className="flex items-start gap-2 text-xs text-slate-700">
                            <span className="text-emerald-600 mt-0.5">✓</span>
                            <span>{t}</span>
                          </li>
                        ))}
                      </ul>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Personalized PRO Plan */}
      {showPersonalized && personalizedPlan && (
        <Card className="border-purple-200 bg-gradient-to-br from-purple-50 to-pink-50">
          <CardContent className="p-6 space-y-6">
            <div className="flex items-center gap-3">
              <div className="bg-purple-600 p-2 rounded-lg">
                <Target className="w-5 h-5 text-white" />
              </div>
              <div>
                <h2 className="text-lg font-bold text-purple-900">Personalized PRO Plan</h2>
                <p className="text-xs text-purple-700">{personalizedPlan.summary}</p>
              </div>
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              {/* Daily Focus */}
              <Card className="border-purple-200 bg-white">
                <CardContent className="p-4 space-y-3">
                  <h3 className="text-sm font-bold text-purple-900">Daily Focus Areas</h3>
                  <ul className="space-y-2">
                    {personalizedPlan.plan.daily_focus.map((item, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-xs text-slate-700">
                        <span className="text-purple-600 mt-0.5">•</span>
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>

              {/* Weekly Goals */}
              <Card className="border-purple-200 bg-white">
                <CardContent className="p-4 space-y-3">
                  <h3 className="text-sm font-bold text-purple-900">Weekly Goals</h3>
                  <ul className="space-y-2">
                    {personalizedPlan.plan.weekly_goals.map((item, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-xs text-slate-700">
                        <span className="text-fuchsia-600 mt-0.5">★</span>
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Today's Focus Snapshot */}
      <Card className="border-blue-200 bg-gradient-to-br from-blue-50 to-indigo-50">
        <CardContent className="p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="bg-blue-600 p-2 rounded-lg">
              <Target className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-blue-900">Today's Focus Snapshot</h2>
              <p className="text-xs text-blue-700">Your minimal daily stack for consistent progress</p>
            </div>
          </div>

          <div className="bg-white rounded-xl border border-blue-200 p-4">
            <ul className="space-y-2" data-testid="mentor-today-checklist">
              <li className="flex items-start gap-3 text-sm text-slate-700">
                <span className="flex-shrink-0 w-5 h-5 bg-emerald-100 text-emerald-700 rounded-full flex items-center justify-center text-xs font-bold">1</span>
                <span>1 Expected Question → practice + send to Mains Engine</span>
              </li>
              <li className="flex items-start gap-3 text-sm text-slate-700">
                <span className="flex-shrink-0 w-5 h-5 bg-fuchsia-100 text-fuchsia-700 rounded-full flex items-center justify-center text-xs font-bold">2</span>
                <span>1 Mains answer using Ranker Rocket Template</span>
              </li>
              <li className="flex items-start gap-3 text-sm text-slate-700">
                <span className="flex-shrink-0 w-5 h-5 bg-indigo-100 text-indigo-700 rounded-full flex items-center justify-center text-xs font-bold">3</span>
                <span>20–30 Prelims MCQs from your weak area</span>
              </li>
            </ul>
          </div>

          {!isPro && (
            <div className="mt-4 bg-amber-50 border border-amber-200 rounded-lg p-3">
              <p className="text-xs text-amber-900">
                💡 <strong>PRO mentor plans</strong> will auto-adjust these stacks to your actual performance and time budget.
              </p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default MentorPage;
